import { useEffect } from "react";
import { Check } from "lucide-react";
import { Seo, Breadcrumbs, breadcrumbSchema, faqSchema } from "@/components/Seo";
import { Reveal, SectionTag } from "@/components/Motion";
import CoopNote from "@/components/CoopNote";
import { ChatButton, FinalCta } from "@/components/Cta";
import FaqAccordion from "@/components/FaqAccordion";
import { SERVICE_PAGES } from "@/data/services";
import { trackEvent } from "@/lib/tracking";
import NotFound from "@/pages/NotFound";

export default function ServiceDetail({ slug }) {
  const svc = SERVICE_PAGES[slug];

  useEffect(() => {
    if (svc) trackEvent("service_view", { service: slug });
  }, [slug, svc]);

  if (!svc) return <NotFound />;

  const crumbs = [
    { name: "首页", path: "/" },
    { name: "我们的服务", path: "/services" },
    { name: svc.kicker, path: `/${svc.slug}` },
  ];

  return (
    <>
      <Seo
        title={svc.seoTitle}
        description={svc.seoDesc}
        path={`/${svc.slug}`}
        schemas={[
          breadcrumbSchema(crumbs),
          faqSchema(svc.faqs),
          {
            "@context": "https://schema.org",
            "@type": "Service",
            name: svc.kicker,
            description: svc.seoDesc,
            provider: { "@id": "https://tkshopmy.com/#organization" },
            areaServed: { "@type": "Country", name: "Malaysia" },
            url: `https://tkshopmy.com/${svc.slug}`,
          },
        ]}
      />

      <section className="relative overflow-hidden pt-28 md:pt-40 pb-16 md:pb-24 grain" data-testid="service-hero">
        <div className="pointer-events-none absolute -right-24 top-10 h-80 w-80 rounded-full bg-lime/25 blur-3xl" />
        <div className="mx-auto max-w-6xl px-5 md:px-8">
          <Breadcrumbs items={crumbs} />
          <Reveal>
            <SectionTag>{svc.kicker}</SectionTag>
            <h1 className="max-w-3xl font-display text-4xl font-medium leading-[1.08] text-balance sm:text-5xl">
              {svc.h1}
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-ink/65 md:text-lg">{svc.intro}</p>
            <div className="mt-8">
              <ChatButton location={`service-${slug}-hero`} variant="primary" testId="service-hero-chat">
                WhatsApp 咨询电商顾问
              </ChatButton>
            </div>
            <p className="mt-4 text-sm text-ink/50">点击按钮，直接 WhatsApp 我们。</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-16 md:py-24">
        <div className="mx-auto grid max-w-6xl gap-12 px-5 md:px-8 lg:grid-cols-2">
          <Reveal>
            <h2 className="font-display text-2xl font-semibold sm:text-3xl">{svc.includesTitle}</h2>
            <ul className="mt-7 space-y-4">
              {svc.includes.map((item) => (
                <li key={item} className="flex gap-3 text-sm leading-relaxed text-ink/70 md:text-base">
                  <Check size={17} className="mt-0.5 shrink-0 text-moss" />
                  {item}
                </li>
              ))}
            </ul>
          </Reveal>
          <div>
            <Reveal delay={0.1}>
              <div className="rounded-2xl bg-forest p-8 text-paper">
                <h2 className="font-display text-2xl font-semibold">这项服务适合谁</h2>
                <ul className="mt-6 space-y-3.5">
                  {svc.whoFor.map((w) => (
                    <li key={w} className="flex gap-3 text-sm leading-relaxed text-paper/75">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-lime" />
                      {w}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.18}>
              <div className="mt-4 rounded-2xl border border-ink/10 bg-paper p-8">
                <h2 className="font-display text-2xl font-semibold">怎样开始</h2>
                <ol className="mt-6 space-y-5">
                  {svc.process.map((p, i) => (
                    <li key={p.title} className="flex gap-4">
                      <span className="font-display text-xl italic text-moss">0{i + 1}</span>
                      <div>
                        <p className="font-semibold text-ink">{p.title}</p>
                        <p className="mt-1 text-sm text-ink/60">{p.desc}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-4xl px-5 md:px-8">
          <Reveal>
            <SectionTag>常见问题</SectionTag>
            <h2 className="font-display text-2xl font-semibold sm:text-3xl">关于这项服务的常见问题</h2>
          </Reveal>
          <Reveal delay={0.1} className="mt-8">
            <FaqAccordion faqs={svc.faqs} testIdPrefix="service-faq" />
          </Reveal>
        </div>
      </section>

      <section className="bg-sand py-10 md:py-12 text-center">
        <div className="mx-auto max-w-3xl px-5 md:px-8">
          <CoopNote
            variant="short"
            title="一件代发 · 无需自行囤货"
            body="除了开店、商品、内容和运营支持，TKShopMY 也提供可了解的一件代发及无需自行囤货合作模式。"
            cta="了解合作方式"
            location={`service-${slug}-coop`}
            testId="service-coop-note"
          />
        </div>
      </section>

      <FinalCta />
    </>
  );
}
